import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { t } from '../../i18n/t';
import { LopButton } from '../../components/LopButton';
import type { MissingGroup } from './dayOpenPrereqs';
import './ValuationParamsModal.css';
import './DayOpenConfirmModal.css';

export interface DayOpenConfirmModalProps {
  groups: MissingGroup[];
  /** Show a tenant heading per group ("open all"). */
  showTenant: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

// Shown before the valuation-params modal when preliminary actions are incomplete.
// Destructive-ish confirm: Escape cancels and focus starts on the cancel button.
export function DayOpenConfirmModal({ groups, showTenant, onConfirm, onClose }: DayOpenConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = t('dayOpen.confirm.title');
  return (
    <div
      className="vparams__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="vparams" role="alertdialog" aria-modal="true" aria-label={title} id="day-open-confirm">
        <header className="vparams__header">
          <h2 className="vparams__title">{title}</h2>
          <button type="button" className="vparams__close" aria-label={t('recordForm.close')} onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="dayopen-confirm__body">
          <p className="dayopen-confirm__text">{t('dayOpen.confirm.text')}</p>
          <div className="dayopen-confirm__list">
            {groups.map((g) => (
              <section key={g.tenantId} className="dayopen-confirm__group">
                {showTenant && <h3 className="dayopen-confirm__tenant">{g.tenantName}</h3>}
                <ul>
                  {g.items.map((it, i) => (
                    <li key={i}>{it}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
        <footer className="vparams__footer">
          <LopButton variant="danger" id="day-open-confirm-proceed" onClick={onConfirm}>
            {t('dayOpen.confirm.proceed')}
          </LopButton>
          <LopButton variant="secondary" id="day-open-confirm-cancel" ref={cancelRef} onClick={onClose}>
            {t('recordForm.cancel')}
          </LopButton>
        </footer>
      </div>
    </div>
  );
}
